import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { PageHeader } from '../../components/common';
import { usePageTitle } from '../../hooks/usePageTitle';
import { ROUTES } from '../../config/routes';

const FAQ_ITEMS = [
  {
    id: 'q1',
    question: 'チャットボットはどうやって作りますか？',
    answer: '「チャットボットを作成」からサイトのURLを入力するだけです。AIが自動でページを読み込み、お客様の質問に答えられるよう学習します。約5分で完成し、埋め込みコードをサイトに貼るだけで公開できます。',
  },
  {
    id: 'q2',
    question: '学習内容を更新するには？',
    answer: 'チャットボット詳細画面の「ファイル」タブから、新しいドキュメントの追加やURLの再学習ができます。更新後は自動的に再学習が開始されます。',
  },
  {
    id: 'q3',
    question: 'リードのCSV書き出しはできますか？',
    answer: 'はい、リード一覧画面からCSVエクスポートが可能です。取得したリードの情報をまとめてダウンロードできます。',
  },
  {
    id: 'q4',
    question: 'プランはいつでも変更できますか？',
    answer: 'はい、いつでもアップグレード・ダウングレードが可能です。変更は即時反映され、日割り計算で精算されます。',
  },
  {
    id: 'q6',
    question: '取得したデータはどこで保管されますか？',
    answer: 'お客様のデータは日本国内で安全に保管されます。通信は暗号化され、お客様ごとに独立して管理されるため、他のお客様とデータが混ざることはありません。',
  },
];

export const FAQPage: React.FC = () => {
  usePageTitle('よくある質問');
  const [expanded, setExpanded] = useState<string | false>('q1');
  const location = useLocation();

  useEffect(() => {
    const h = location.hash.replace('#', '');
    if (FAQ_ITEMS.some(i => i.id === h)) {
      setExpanded(h);
      setTimeout(() => {
        const el = document.getElementById(h);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [location.hash]);

  return (
    <div data-testid="faq-page">
      <PageHeader title="ヘルプ・よくある質問" />
      <p className="text-sm text-gray-500 mb-6">
        よくいただくご質問をまとめました。解決しない場合はお問い合わせください。
      </p>

      <div className="flex flex-col gap-3 max-w-[800px]">
        {FAQ_ITEMS.map(item => {
          const isExpanded = expanded === item.id;
          return (
            <div
              key={item.id}
              id={item.id}
              className="bg-white border border-[#E2E8F0] rounded-[12px] overflow-hidden"
              data-testid="faq-item"
            >
              <button
                onClick={() => setExpanded(isExpanded ? false : item.id)}
                className="w-full flex items-center justify-between px-6 py-4 cursor-pointer text-left"
                style={{ background: isExpanded ? '#F0FDFA' : '#fff' }}
                data-testid="faq-item-question"
              >
                <span className="text-[15px] font-bold text-[#0F172A]">{item.question}</span>
                {isExpanded
                  ? <ChevronUp size={20} stroke="#0a7a72" className="flex-shrink-0 ml-3" />
                  : <ChevronDown size={20} stroke="#94A3B8" className="flex-shrink-0 ml-3" />
                }
              </button>
              {isExpanded && (
                <div
                  className="px-6 pt-3 pb-5 text-sm leading-relaxed text-[#334155] whitespace-pre-line"
                  data-testid="faq-item-answer"
                >
                  {item.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* CTA card */}
      <div
        className="max-w-[800px] mt-6 rounded-[12px] text-center py-8 px-6"
        style={{ background: '#F0FDFA' }}
      >
        <p className="text-base font-bold text-[#0F172A] mb-1">解決しませんでしたか？</p>
        <p className="text-sm text-gray-500 mb-5">サポートチームがお答えします。</p>
        <Link
          to={ROUTES.CONTACT}
          className="inline-block px-6 py-2.5 rounded-lg text-sm font-bold text-white"
          style={{ background: '#0a7a72' }}
        >
          お問い合わせ
        </Link>
      </div>
    </div>
  );
};
